import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useAuthStore } from '../store';
import { HelpCircle, MessageCircle, Send } from 'lucide-react';
import { toast } from 'sonner';

interface QA {
  id: string;
  productId: string;
  vendorId: string;
  customerId: string;
  customerName: string;
  question: string;
  answer?: string;
  createdAt: any;
  answeredAt?: any;
}

export function ProductQA({ productId, vendorId }: { productId: string; vendorId?: string }) {
  const { profile } = useAuthStore();
  const [qas, setQas] = useState<QA[]>([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!productId) return;
    
    // We only want to show answered questions OR questions asked by this specific user
    const q = query(
      collection(db, 'product_qa'),
      where('productId', '==', productId)
      // orderBy requires a composite index with where, so we'll sort locally
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedQas = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as QA));
      // Sort by answered questions first or newer
      fetchedQas.sort((a, b) => {
        const timeA = a.createdAt?.toMillis?.() || 0;
        const timeB = b.createdAt?.toMillis?.() || 0;
        return timeB - timeA;
      });
      setQas(fetchedQas);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [productId]);

  const handleAskQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newQuestion.trim() || !auth.currentUser || !vendorId) {
      if (!auth.currentUser) toast.error('Please log in to ask a question');
      return;
    }

    try {
      await addDoc(collection(db, 'product_qa'), {
        productId,
        vendorId,
        customerId: auth.currentUser.uid,
        customerName: profile?.displayName || auth.currentUser.email || 'Customer',
        question: newQuestion.trim(),
        createdAt: serverTimestamp(),
      });
      setNewQuestion('');
      toast.success('Question submitted! The vendor will answer soon.');
    } catch (err) {
      console.error(err);
      toast.error('Failed to submit question');
    }
  };

  const visibleQas = qas.filter(qa => qa.answer || qa.customerId === auth.currentUser?.uid);

  return (
    <div className="mt-16 border-t border-neutral-200 dark:border-neutral-800 pt-16">
      <div className="flex items-center gap-3 mb-8">
        <HelpCircle className="w-6 h-6 text-neutral-900 dark:text-white" />
        <h2 className="text-2xl font-serif font-medium text-neutral-900 dark:text-white">Customer Questions & Answers</h2>
      </div>

      <div className="bg-neutral-50 dark:bg-neutral-900/50 rounded-2xl p-6 md:p-8 border border-neutral-100 dark:border-neutral-800 mb-12">
        {auth.currentUser ? (
          <form onSubmit={handleAskQuestion} className="flex gap-3">
            <input
              type="text"
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
              placeholder="Have a question about this product?"
              className="flex-1 px-4 py-3 bg-white dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white transition-shadow text-neutral-900 dark:text-white"
            />
            <button 
              type="submit" 
              disabled={!newQuestion.trim()}
              className="px-6 py-3 bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 font-medium text-sm rounded-xl hover:bg-neutral-800 dark:hover:bg-neutral-100 transition-colors disabled:opacity-50 shrink-0 flex items-center gap-2"
            >
              Ask <Send className="w-4 h-4 hidden sm:block" />
            </button>
          </form>
        ) : (
          <div className="text-center py-4 text-sm text-neutral-500 dark:text-neutral-400">
            Please log in to ask a question.
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900 dark:border-white"></div>
        </div>
      ) : visibleQas.length === 0 ? (
        <div className="text-center py-12 text-neutral-500 dark:text-neutral-400 border border-dashed border-neutral-200 dark:border-neutral-800 rounded-2xl">
          <MessageCircle className="w-8 h-8 mx-auto mb-3 opacity-50" />
          <p>No questions yet. Be the first to ask!</p>
        </div>
      ) : (
        <div className="space-y-8">
          {visibleQas.map(qa => (
            <div key={qa.id} className="border-b border-neutral-100 dark:border-neutral-800 pb-8 last:border-0 last:pb-0">
              <div className="flex gap-4 mb-4">
                <div className="w-8 flex justify-center text-lg font-bold text-neutral-400 dark:text-neutral-600">Q:</div>
                <div>
                  <p className="text-base font-medium text-neutral-900 dark:text-white leading-relaxed">{qa.question}</p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                    Asked by {qa.customerName} {qa.createdAt?.toDate ? `on ${qa.createdAt.toDate().toLocaleDateString()}` : ''}
                  </p>
                </div>
              </div>
              
              {qa.answer ? (
                <div className="flex gap-4">
                  <div className="w-8 flex justify-center text-lg font-bold text-indigo-500 dark:text-indigo-400">A:</div>
                  <div className="bg-white dark:bg-neutral-900 p-4 rounded-xl border border-neutral-100 dark:border-neutral-800 w-full">
                    <p className="text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed">{qa.answer}</p>
                    {qa.answeredAt && (
                      <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-2">
                        Answered on {qa.answeredAt.toDate().toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex gap-4">
                  <div className="w-8"></div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400 italic bg-neutral-50 dark:bg-neutral-900/30 px-3 py-1.5 rounded-md inline-block">
                    Awaiting vendor response...
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
